import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Chip,
  Alert,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableFooter,
  Skeleton,
  useTheme,
  alpha,
  Avatar,
  Stack,
} from '@mui/material';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  IndeterminateCheckBox,
  ReceiptLong as ReceiptLongIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import SysDateRangePicker from '../../components/SysDateRangePicker';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import InvoiceViewModal from '../../components/modals/InvoiceViewModal';
import NfcomGerarLoteModal from '../../components/modals/NfcomGerarLoteModal';
import FormaPgtoSelect from '../../components/selects/FormaPgtoSelect';
import EscritorioSelect from '../../components/selects/EscritorioSelect';
import GatewaySelect from '../../components/selects/GatewaySelect';
import TipoFaturaSelect from '../../components/selects/TipoFaturaSelect';
import invoiceService, { InvoiceFilters } from '../../services/invoiceService';
import { nfcomService } from '../../services/nfcomService';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import { Invoice } from '../../types';
import { formatarData, formatarValor } from '../../utils/formatToView';
import { InvoiceStatusChip } from '../../utils/faturaStatusUtils';

const TABLE_COLOR = '#1976d2';

const NfcomGerarLotePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  // Filtros
  const [vencRange, setVencRange] = useState<[Date | null, Date | null]>([null, null]);
  const [pagRange, setPagRange] = useState<[Date | null, Date | null]>([null, null]);
  // estados legacy removidos (vencSelection/pagSelection) – o picker atual retorna { startDate, endDate }
  const [formaPgto, setFormaPgto] = useState<string>('');
  const [tipoFatura, setTipoFatura] = useState<string>('');
  const [somentePagos, setSomentePagos] = useState<boolean>(false);
  const [ignoraParcialmentePago, setIgnoraParcialmentePago] = useState<boolean>(false);
  const [escritorioId, setEscritorioId] = useState<number | number[] | null>(null);
  const [geranfcom, setGeranfcom] = useState<boolean>(true);
  const [gatewayId, setGatewayId] = useState<number | null>(null);

  // Estado de resultados
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [viewOpen, setViewOpen] = useState<boolean>(false);
  const [viewInvoiceId, setViewInvoiceId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [gerarModalOpen, setGerarModalOpen] = useState<boolean>(false);
  type EmitState = { status: string; message?: string };
  const [emitById, setEmitById] = useState<Record<number, EmitState>>({});

  // Estados de paginação
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalValorBackend, setTotalValorBackend] = useState<number>(0);

  const setEmit = (fid: number, state: EmitState) => {
    setEmitById(prev => ({ ...prev, [fid]: state }));
  };

  const renderEmitChip = (state?: EmitState) => {
    if (!state) return <Chip label="-" size="small" variant="outlined" />;
    const s = state.status;
    const msg = state.message;
    if (s === 'processando') return <Chip label="Processando" color="warning" size="small" />;
    if (s === 'autorizado') return <Chip label="autorizado" color="success" size="small" />;
    if (s === 'rejeitado' || s === 'erro') {
      return (
        <Box>
          <Chip label={s} color="error" size="small" />
          {msg && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {msg}
            </Typography>
          )}
        </Box>
      );
    }
    return <Chip label={String(s)} size="small" variant="outlined" />;
  };

  const handleBuscar = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validação obrigatória: preencher (vencimento início e fim) OU (pagamento início e fim)
      const [vencIni, vencFim] = vencRange;
      const [pagIni, pagFim] = pagRange;
      const venceuPreenchido = !!vencIni && !!vencFim;
      const pagPreenchido = !!pagIni && !!pagFim;

      if (!venceuPreenchido && !pagPreenchido) {
        setLoading(false);
        setError('Preencha Vencimento (início e fim) ou Pagamento (início e fim).');
        return;
      }

      // Validação de intervalos: início <= fim
      if (venceuPreenchido && vencIni && vencFim && vencIni > vencFim) {
        setLoading(false);
        setError('O intervalo de Vencimento é inválido: a data inicial é maior que a final.');
        return;
      }
      if (pagPreenchido && pagIni && pagFim && pagIni > pagFim) {
        setLoading(false);
        setError('O intervalo de Pagamento é inválido: a data inicial é maior que a final.');
        return;
      }

      const filters: InvoiceFilters = {
        // Paginação (page no backend começa em 1, no frontend em 0)
        page: page + 1,
        limit: rowsPerPage,
        // status e ranges serão tratados no backend se suportado
      };

      // Se o filtro de pagamento está preenchido, garantir que buscamos apenas faturas pagas
      if (pagPreenchido || somentePagos) {
        filters.status = 'pago';
      }

      if (gatewayId) filters.gateway_id = gatewayId;

      // Filtro de tipo de fatura (enviado para o backend)
      if (tipoFatura) {
        filters.tipo = tipoFatura;
      }

      // Filtro de forma de pagamento (enviado para o backend se somentePagos ativo)
      if (somentePagos && formaPgto) {
        filters.forma_pagamento = formaPgto;
      }

      //ignorar faturas com nfcom_id
      filters.sem_nfcom = true;

      // Se geranfcom = true, manter somente faturas cujo contrato possui geranfcom = true
      if (geranfcom) {
        filters.geranfcom_contrato = true;
      }

      // Filtro de data de vencimento
      if (venceuPreenchido) {
        if (vencIni) filters.data_inicio = vencIni.toISOString();
        if (vencFim) filters.data_fim = vencFim.toISOString();
      }

      // Filtro de data de pagamento (enviado para o backend)
      if (pagPreenchido && pagIni && pagFim) {
        const fmtDate = (d: Date) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        filters.data_pagamento_inicio = fmtDate(pagIni);
        filters.data_pagamento_fim = fmtDate(pagFim);
      }

      const resp = await invoiceService.getInvoices(filters);
      let data = resp.data || [];

      // Atualizar informações de paginação
      if (resp.pagination) {
        setTotalCount(resp.pagination.total || 0);
      }
      if ((resp as any).summary) {
        setTotalValorBackend(Number((resp as any).summary.total_valor) || 0);
      }

      // Filtrar por escritório se informado (Invoice tem escritorio_id opcional)
      if (escritorioId) {
        if (Array.isArray(escritorioId)) {
          const setIds = new Set(escritorioId);
          data = data.filter((inv: any) => inv?.escritorio_id && setIds.has(inv.escritorio_id));
        } else {
          data = data.filter((inv: any) => Number(inv?.escritorio_id) === Number(escritorioId));
        }
      }

      // Excluir canceladas
      data = data.filter((inv: any) => inv?.status !== 'cancelado');

      //ignorar faturas com status = parcialmente_pago
      if (ignoraParcialmentePago) {
        data = data.filter((inv: any) => inv?.status !== 'parcialmente_pago');
      }

      setInvoices(data);
      setHasSearched(true);
    } catch (err: any) {
      console.error('Erro ao buscar faturas:', err);
      setError('Erro ao buscar faturas para geração em lote');
    } finally {
      setLoading(false);
    }
  };

  const totalSelecionadas = useMemo(() => invoices.length, [invoices]);
  const allIds = useMemo(() => invoices.map((i) => i.id), [invoices]);
  const selectedTotalValor = useMemo(() => {
    const sum = invoices.reduce((acc, inv) => acc + (selectedIds.includes(inv.id) ? Number(inv.valor || 0) : 0), 0);
    return invoiceService.formatarValor(sum);
  }, [selectedIds, invoices]);
  const handleEmitirLote = async (params: { data_emissao: string; escritorio_id?: number | null; descricao_template: string; faturaIds: number[]; cclass: string; }) => {
    const { data_emissao, escritorio_id, descricao_template, faturaIds, cclass } = params;
    // Inicializa status de todas as faturas selecionadas como 'processando'
    setEmitById(prev => {
      const next = { ...prev } as Record<number, EmitState>;
      faturaIds.forEach(id => { next[id] = { status: 'processando' }; });
      return next;
    });

    // Sweet de aviso para aguardar (não bloquear a execução)
    Swal.fire({
      title: 'Gerando notas... ',
      html: 'Por favor, aguarde. Não feche nem saia desta página até a conclusão.',
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
      showConfirmButton: false,
    });

    try {
      // Processa em série para simplificar; pode ser paralelizado se necessário
      for (const fid of faturaIds) {
        try {
          // chamada ao backend stub via nfcomService
          const payload: any = { data_emissao, fatura_id: fid, escritorio_id: escritorio_id || undefined, descricao_template, cclass };
          const resp = await nfcomService.gerarLote(payload);
          console.log('Resposta da geração de nota em lote:', resp);
          const success = (resp as any)?.success ?? true;
          const backendStatus = (resp as any)?.status as string | undefined;
          const backendMessage = (resp as any)?.message as string | undefined;
          if (success) {
            setEmit(fid, { status: backendStatus || 'autorizado', message: backendMessage });
          } else {
            const msg = backendMessage || (resp as any)?.error || 'Falha ao gerar NFCom';
            setEmit(fid, { status: backendStatus || 'rejeitado', message: String(msg) });
          }
        } catch (e: any) {
          const respData = e?.response?.data;
          const backendStatus = (respData?.status as string | undefined) || undefined;
          const backendMessage = (respData?.message as string | undefined) || (respData?.error as string | undefined) || e?.message || 'Erro ao gerar NFCom';
          const statusToSet = backendStatus || (respData?.success === false ? 'rejeitado' : 'erro');
          setEmit(fid, { status: statusToSet, message: String(backendMessage) });
        }
      }

      // Atualiza a listagem após concluir
      await handleBuscar();
    } finally {
      Swal.close();
    }
  };
  const selectedTotalValorRaw = useMemo(() => {
    return invoices.reduce((acc, inv) => acc + (selectedIds.includes(inv.id) ? Number(inv.valor || 0) : 0), 0);
  }, [selectedIds, invoices]);

  const totalValorRegistros = useMemo(() => {
    return invoices.reduce((acc, inv) => acc + Number(inv.valor || 0), 0);
  }, [invoices]);

  useEffect(() => {
    // Limpar seleção quando os resultados mudarem
    setSelectedIds([]);
  }, [invoices]);

  // Estado para controlar se já foi feita uma busca inicial
  const [hasSearched, setHasSearched] = useState(false);

  // Resetar página quando os filtros mudarem (exceto page e rowsPerPage)
  useEffect(() => {
    setPage(0);
    setHasSearched(false);
  }, [vencRange, pagRange, formaPgto, tipoFatura, somentePagos, ignoraParcialmentePago, escritorioId, geranfcom, gatewayId]);

  // Buscar novamente quando a página ou rowsPerPage mudarem (após busca inicial)
  useEffect(() => {
    if (hasSearched) {
      handleBuscar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleToggleRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAll = async () => {
    // Verificar quantos registros da página atual estão selecionados
    const selectedInPage = invoices.filter(inv => selectedIds.includes(inv.id)).length;
    const allInPageSelected = selectedInPage === invoices.length && invoices.length > 0;

    // Se todos da página atual estão selecionados, desmarcar apenas os da página atual
    if (allInPageSelected) {
      const idsToRemove = new Set(invoices.map(inv => inv.id));
      setSelectedIds(prev => prev.filter(id => !idsToRemove.has(id)));
      return;
    }

    // Perguntar se deseja selecionar apenas a página atual ou todos os registros
    const result = await Swal.fire({
      title: 'Selecionar Registros',
      html: `
        <div style="text-align: center;">
          <p style="font-size: 16px; margin-bottom: 15px;">
            Deseja selecionar apenas os registros da página atual ou todos os registros?
          </p>
          <p style="font-size: 14px; color: #666; margin-bottom: 20px;">
            <strong>Página atual:</strong> ${invoices.length} registros<br/>
            <strong>Total de registros:</strong> ${totalCount} registros
          </p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Página Atual',
      denyButtonText: 'Todos os Registros',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      denyButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
      customClass: {
        popup: 'swal-zindex',
        container: 'swal-zindex'
      }
    });

    if (result.isDismissed) {
      // Cancelou
      return;
    }

    if (result.isConfirmed) {
      // Selecionar apenas a página atual
      setSelectedIds(allIds);
    } else if (result.isDenied) {
      // Selecionar todos os registros (de todas as páginas)
      try {
        setLoading(true);

        // Buscar todos os registros sem paginação
        const filters: InvoiceFilters = {
          page: 1,
          limit: totalCount || 10000, // Limite alto para pegar todos
        };

        // Aplicar os mesmos filtros da busca atual
        const [vencIni, vencFim] = vencRange;
        const [pagIni, pagFim] = pagRange;
        const venceuPreenchido = !!vencIni && !!vencFim;
        const pagPreenchido = !!pagIni && !!pagFim;

        if (pagPreenchido || somentePagos) {
          filters.status = 'pago';
        }

        if (gatewayId) filters.gateway_id = gatewayId;

        if (tipoFatura) {
          filters.tipo = tipoFatura;
        }

        if (somentePagos && formaPgto) {
          filters.forma_pagamento = formaPgto;
        }

        filters.sem_nfcom = true;

        if (geranfcom) {
          filters.geranfcom_contrato = true;
        }

        if (venceuPreenchido) {
          if (vencIni) filters.data_inicio = vencIni.toISOString();
          if (vencFim) filters.data_fim = vencFim.toISOString();
        }

        if (pagPreenchido && pagIni && pagFim) {
          const fmtDate = (d: Date) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          filters.data_pagamento_inicio = fmtDate(pagIni);
          filters.data_pagamento_fim = fmtDate(pagFim);
        }

        const resp = await invoiceService.getInvoices(filters);
        let allData = resp.data || [];

        // Aplicar os mesmos filtros do frontend
        if (escritorioId) {
          if (Array.isArray(escritorioId)) {
            const setIds = new Set(escritorioId);
            allData = allData.filter((inv: any) => inv?.escritorio_id && setIds.has(inv.escritorio_id));
          } else {
            allData = allData.filter((inv: any) => Number(inv?.escritorio_id) === Number(escritorioId));
          }
        }

        allData = allData.filter((inv: any) => inv?.status !== 'cancelado');

        if (ignoraParcialmentePago) {
          allData = allData.filter((inv: any) => inv?.status !== 'parcialmente_pago');
        }

        // Selecionar todos os IDs
        const allIdsToSelect = allData.map((inv: Invoice) => inv.id);
        setSelectedIds(allIdsToSelect);
      } catch (err: any) {
        console.error('Erro ao buscar todos os registros:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: 'Erro ao buscar todos os registros. Tente novamente.',
          customClass: {
            popup: 'swal-zindex',
            container: 'swal-zindex'
          }
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenView = (id: number) => {
    setViewInvoiceId(id);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setViewInvoiceId(null);
  };

  const handleVencChange = (range: { startDate: Date | null; endDate: Date | null }) => {
    const start = range.startDate ? new Date(range.startDate) : null;
    const end = range.endDate ? new Date(range.endDate) : null;
    setVencRange([start, end]);
  };

  const handlePagChange = (range: { startDate: Date | null; endDate: Date | null }) => {
    const start = range.startDate ? new Date(range.startDate) : null;
    const end = range.endDate ? new Date(range.endDate) : null;
    setPagRange([start, end]);
  };

  return (
    <Box sx={{ p: 1 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, width: 40, height: 40 }}>
              <ReceiptLongIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                NFCom - Gerar em Lote
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gerar notas fiscais em lote
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/notas-nfcom')}>Voltar</Button>
          </Stack>
        </Box>
        <Paper sx={{ p: 1.5 }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={2}>
              <EscritorioSelect value={escritorioId} showTodosOption onChange={setEscritorioId} label="Escritório" size="small" allowClear />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <SysDateRangePicker
                label="Data de Vencimento"
                onChange={handleVencChange}
              />
            </Grid>
            {somentePagos && (
              <Grid item xs={12} sm={6} md={2}>
                <SysDateRangePicker
                  label="Data de Pagamento"
                  onChange={handlePagChange}
                />
              </Grid>
            )}

            {somentePagos && (
              <Grid item xs={12} sm={6} md={2}>
                <FormaPgtoSelect
                  value={formaPgto}
                  onChange={setFormaPgto}
                  label="Forma de Pagamento"
                  size="small"
                  allowClear
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={2}>
              <GatewaySelect value={gatewayId || ''} onChange={(id) => setGatewayId(id)} label="Gateway de Cobrança" size="small" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TipoFaturaSelect value={tipoFatura} onChange={setTipoFatura} label="Tipo de Fatura" size="small" allowClear />
            </Grid>
          </Grid>
          <Grid container spacing={0} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4} display="flex" alignItems="center">
              <FormControlLabel control={<Checkbox checked={geranfcom} onChange={(e) => setGeranfcom(e.target.checked)} />} label="Somente Faturas com Gerar NFCom habilitado no contrato" />
            </Grid>
            <Grid item xs={12} md={1.5} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={somentePagos}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setSomentePagos(checked);
                      if (!checked) {
                        setFormaPgto('');
                        setPagRange([null, null]);
                      }
                    }}
                  />
                }
                label="Somente pagos"
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" alignItems="center">
              <FormControlLabel control={<Checkbox checked={ignoraParcialmentePago} onChange={(e) => setIgnoraParcialmentePago(e.target.checked)} />} label="Ignorar parcialmente pago" />
            </Grid>
            <Grid item xs={12} md={4.5} display="flex" alignItems="center" justifyContent="flex-end">
              <Button variant="contained" onClick={handleBuscar}>Buscar</Button>
            </Grid>
          </Grid>
        </Paper>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: { xs: 0.5, sm: 0 },
            px: 2,
            py: 1,
            bgcolor: alpha(TABLE_COLOR, 0.08),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 1,
              mb: { xs: 0.5, sm: 0 }
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
              Resultados
            </Typography>
            <Typography component="span" variant="caption" color="text.secondary" sx={{ fontSize: '0.74rem' }}>
              <strong>{totalCount}</strong> registro(s) — Valor total: <strong>{formatarValor(totalValorBackend)}</strong>
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              alignItems: { xs: 'stretch', sm: 'center' },
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Chip
              label={`Selecionadas: ${selectedIds.length} (${selectedTotalValor})`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ mb: { xs: 0.5, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              disabled={selectedIds.length === 0}
              onClick={() => setGerarModalOpen(true)}
              sx={{
                textTransform: 'none',
                fontSize: '0.78rem',
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Gerar
            </Button>
          </Box>
        </Box>


        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t: any) => `color-mix(in srgb, ${TABLE_COLOR} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    borderBottom: `2px solid ${alpha(TABLE_COLOR, 0.2)}`,
                  },
                }}
              >
                <TableCell sx={{ width: 40, px: 0.5 }}>
                  {(() => {
                    const selectedInPage = invoices.filter(inv => selectedIds.includes(inv.id)).length;
                    const allInPageSelected = selectedInPage === invoices.length && invoices.length > 0;
                    const someInPageSelected = selectedInPage > 0 && selectedInPage < invoices.length;
                    return (
                      <IconButton size="small" onClick={handleToggleAll} sx={{ padding: 0 }}>
                        {allInPageSelected ? (
                          <CheckBox fontSize="small" color="primary" />
                        ) : someInPageSelected ? (
                          <IndeterminateCheckBox fontSize="small" color="primary" />
                        ) : (
                          <CheckBoxOutlineBlank fontSize="small" />
                        )}
                      </IconButton>
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ width: 60 }}>ID</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell sx={{ width: 100 }}>Contrato</TableCell>
                <TableCell sx={{ width: 100 }}>Tipo</TableCell>
                <TableCell sx={{ width: 100 }}>Vencimento</TableCell>
                <TableCell sx={{ width: 100 }}>Pagamento</TableCell>
                <TableCell sx={{ width: 100 }}>Valor</TableCell>
                <TableCell sx={{ width: 110 }}>Status</TableCell>
                <TableCell sx={{ width: 150 }}>Emissão NFCom</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.disabled">
                      Nenhum registro encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((inv) => (
                  <TableRow key={inv.id} hover sx={{ '&:hover': { bgcolor: alpha(TABLE_COLOR, 0.03) } }}>
                    <TableCell sx={{ py: 0.5, px: 0.5 }}>
                      <IconButton size="small" onClick={() => handleToggleRow(inv.id)} sx={{ padding: 0 }}>
                        {selectedIds.includes(inv.id) ? (
                          <CheckBox fontSize="small" color="primary" />
                        ) : (
                          <CheckBoxOutlineBlank fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{ py: 0.5, fontSize: '0.78rem', cursor: 'pointer', color: '#1976d2' }}
                      onClick={() => handleOpenView(inv.id)}
                    >
                      #{inv.id}
                    </TableCell>
                    <TableCell
                      sx={{ py: 0.5, fontSize: '0.78rem', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1976d2' }}
                      onClick={() => {
                        const cid = (inv as any)?.cliente?.id ?? inv.cliente_id;
                        if (cid) navigate(`/clients/${cid}`);
                      }}
                    >
                      {(inv as any)?.cliente?.nome ?? inv.cliente_id ?? '-'}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {(() => {
                        const contratoId = (inv as any)?.contrato?.id ?? inv.contrato_id;
                        return contratoId ? (
                          <RouterLink
                            to={`/contracts/${contratoId}`}
                            style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 500 }}
                          >
                            #{contratoId}
                          </RouterLink>
                        ) : '-';
                      })()}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {invoiceService.formatType(inv.tipo)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {formatarData(inv.data_vencimento)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      {inv.data_pagamento ? formatarData(inv.data_pagamento) : '-'}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, fontSize: '0.78rem', fontWeight: 600 }}>
                      {formatarValor(Number(inv.valor || 0))}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <InvoiceStatusChip status={inv.status} />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {renderEmitChip(emitById[inv.id])}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {!loading && invoices.length > 0 && (
              <TableFooter sx={{ bgcolor: 'background.paper' }}>
                <TableRow
                  sx={{
                    '& .MuiTableCell-root': {
                      bgcolor: (t: any) => `color-mix(in srgb, ${TABLE_COLOR} 15%, ${t.palette.background.paper})`,
                      fontWeight: 800,
                      fontSize: '0.78rem',
                      borderTop: `2px solid ${alpha(TABLE_COLOR, 0.3)}`,
                      py: 1.2,
                      color: 'text.primary',
                    },
                  }}
                >
                  <TableCell colSpan={7} align="right" sx={{ letterSpacing: '0.05em' }}>
                    TOTAL
                  </TableCell>
                  <TableCell>
                    {formatarValor(totalValorRegistros)}
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>

        {totalCount > 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 0.8,
              px: 2,
              bgcolor: alpha(TABLE_COLOR, 0.04),
              borderTop: `1px solid ${alpha(TABLE_COLOR, 0.15)}`,
            }}
          >
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por pág:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                width: '100%',
                '.MuiTablePagination-toolbar': {
                  minHeight: 32,
                  flexWrap: 'nowrap',
                  justifyContent: 'center',
                  gap: 1,
                  p: 0,
                  '& .MuiTablePagination-spacer': { display: 'none' },
                },
                '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
                '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
                '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
              }}
            />
          </Box>
        )}
      </Paper>
      {viewInvoiceId !== null && (
        <InvoiceViewModal
          open={viewOpen}
          onClose={handleCloseView}
          invoice_id={viewInvoiceId}
        />
      )}
      <NfcomGerarLoteModal
        open={gerarModalOpen}
        onClose={() => setGerarModalOpen(false)}
        faturaIds={selectedIds}
        totalValor={`R$ ${selectedTotalValorRaw.toFixed(2)}`}
        onConfirm={handleEmitirLote}
        onSuccess={() => {
          // Após gerar, podemos opcionalmente atualizar a lista
          handleBuscar();
        }}
      />
    </Box>
  );
};

export default NfcomGerarLotePage;


